import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import classNames from "classnames";
import { createContext, memo, useState, useCallback, useMemo, useContext, useRef, useEffect } from "react";
import { usePopper } from "react-popper";
import { Portal } from "./Portal.js";
import { useCloseOnEscape, useCloseOnClickOutside } from "../auxiliary/useCloseOnEscapeOrClickOutside.js";
import { Collapsible } from "./Collapsible/Collapsible.js";
import { assertNever } from "../utils/assertNever.js";
import { useClassNamePrefix } from "../auxiliary/useClassNamePrefix.js";
import { Button } from "./Forms/Button/Button.js";
import { toViewClass } from "../utils/toViewClass.js";
const alignmentToPlacement = (alignment) => {
  if (alignment === "start") {
    return "bottom-start";
  } else if (alignment === "end") {
    return "bottom-end";
  } else if (alignment === "center" || alignment === "default" || alignment === void 0) {
    return "bottom";
  } else if (alignment === "top") {
    return "top";
  } else if (alignment === "right") {
    return "right";
  } else {
    return assertNever();
  }
};
const noop = () => {
};
const DropdownContentContainerContext = createContext(void 0);
DropdownContentContainerContext.displayName = "DropdownContentContainerContext";
const Dropdown = memo((props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const onDismiss = props.onDismiss;
  const isActive = isOpen || isTransitioning;
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const placement = alignmentToPlacement(props.alignment);
  const { styles, attributes, forceUpdate, update } = usePopper(
    isActive ? referenceElement : null,
    isActive ? popperElement : null,
    { placement, strategy: props.strategy }
  );
  const suppliedButtonOnClickHandler = props.buttonProps && props.buttonProps.onClick;
  const onButtonClick = useCallback(
    (e) => {
      forceUpdate?.();
      setIsOpen((isOpen2) => !isOpen2);
      setIsTransitioning(true);
      suppliedButtonOnClickHandler && suppliedButtonOnClickHandler(e);
    },
    [forceUpdate, suppliedButtonOnClickHandler]
  );
  const close = useCallback(() => {
    setIsOpen(false);
    setIsTransitioning(true);
  }, []);
  const dismiss = useCallback(() => {
    close();
    onDismiss?.();
  }, [close, onDismiss]);
  useCloseOnEscape({ isOpen, close: dismiss });
  useCloseOnClickOutside({ isOpen, close: dismiss, contents: useMemo(() => [referenceElement, popperElement], [referenceElement, popperElement]) });
  const contentContainerFromContent = useContext(DropdownContentContainerContext);
  const contentContainer = props.contentContainer || contentContainerFromContent || document.body;
  const prefix = useClassNamePrefix();
  const { children, renderContent, renderToggle, styledContent = true } = props;
  const renderProps = {
    requestClose: close,
    forceUpdate: forceUpdate ?? noop,
    update: update ?? noop
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    renderToggle ? renderToggle({ ref: setReferenceElement, onClick: onButtonClick }) : /* @__PURE__ */ jsx(Button, { ...props.buttonProps, onClick: onButtonClick, ref: setReferenceElement }),
    isActive && /* @__PURE__ */ jsx(Portal, { to: contentContainer, children: /* @__PURE__ */ jsx(
      "div",
      {
        ref: setPopperElement,
        style: styles.popper,
        ...attributes.popper,
        className: `${prefix}dropdown-content`,
        "data-placement": placement,
        children: /* @__PURE__ */ jsx(
          Collapsible,
          {
            expanded: isOpen,
            transition: "fade",
            onTransitionEnd: () => {
              if (isOpen) {
                return;
              }
              setIsTransitioning(false);
            },
            children: renderContent ? renderContent(renderProps) : /* @__PURE__ */ jsx("div", { className: classNames(`${prefix}dropdown-content-in`, toViewClass("unstyled", !styledContent)), children: typeof children === "function" ? children(renderProps) : children })
          }
        )
      }
    ) })
  ] });
});
Dropdown.displayName = "Dropdown";
const DropdownContentContainerProvider = memo((props) => {
  const [contentContainer, setContentContainer] = useState(void 0);
  const contentContainerRef = useRef(null);
  useEffect(() => {
    setContentContainer(contentContainerRef.current || void 0);
  }, []);
  const prefix = useClassNamePrefix();
  return /* @__PURE__ */ jsx("div", { className: `${prefix}dropdown-contentContainer`, ref: contentContainerRef, children: /* @__PURE__ */ jsx(DropdownContentContainerContext.Provider, { value: contentContainer, children: props.children }) });
});
DropdownContentContainerProvider.displayName = "DropdownContentContainerProvider";
export {
  Dropdown,
  DropdownContentContainerContext,
  DropdownContentContainerProvider
};
//# sourceMappingURL=Dropdown.js.map
